(function () {
  let internalBisRequest = false;

  const config = () => window.__klaviyoBisConfig || {};
  const suffix = () => window.__klaviyoBisSuffix || 'en-UK';
  const companyId = () => config().companyId || 'WExNFE';
  const apiUrl = () =>
    `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${companyId()}`;
  const API_REVISION = '2024-06-15';

  function toCatalogVariantId(productId, variantId) {
    return `$custom:::$default:::${productId}_${variantId}_${suffix()}`;
  }

  function getSelectedVariantId() {
    const input = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    if (input && input.value) return Number(input.value);
    return config().selectedVariantId || null;
  }

  function findBisModal(node) {
    return (
      node?.closest('#klaviyo-bis-modal') ||
      node?.closest('[id*="klaviyo-bis-modal"]') ||
      node?.closest('.klaviyo-bis-modal') ||
      null
    );
  }

  function findBisEmailInput(modal) {
    return modal?.querySelector('input[type="email"]') || null;
  }

  function findBisSubmitButtons(modal) {
    if (!modal) return [];
    return Array.from(
      modal.querySelectorAll('button[type="submit"], input[type="submit"], button:not([type="button"])'),
    );
  }

  function setBisBusy(modal, busy) {
    modal.classList.toggle('klaviyo-bis-modal--loading', busy);

    findBisSubmitButtons(modal).forEach((el) => {
      el.disabled = busy;
      el.setAttribute('aria-busy', busy ? 'true' : 'false');

      if (busy) {
        if (!el.dataset.klaviyoBisOriginalText) {
          el.dataset.klaviyoBisOriginalText = el.textContent.trim();
        }
        el.textContent = config().submittingLabel || 'Submitting...';
      } else if (el.dataset.klaviyoBisOriginalText) {
        el.textContent = el.dataset.klaviyoBisOriginalText;
      }
    });
  }

  function showFormError(modal, message) {
    let errorEl = modal.querySelector('[data-klaviyo-bis-error]');
    if (!errorEl) {
      errorEl = document.createElement('p');
      errorEl.dataset.klaviyoBisError = 'true';
      errorEl.setAttribute('role', 'alert');
      errorEl.style.marginTop = '12px';
      modal.appendChild(errorEl);
    }
    errorEl.textContent = message;
  }

  function clearFormError(modal) {
    modal.querySelector('[data-klaviyo-bis-error]')?.remove();
  }

  function showSuccess(modal) {
    const successLabel = config().subscriptionSuccessLabel || "You're in! We'll let you know when it's back.";

    modal.classList.remove('klaviyo-bis-modal--loading');
    modal.classList.add('klaviyo-bis-modal--success');

    modal.querySelector('[data-klaviyo-bis-error]')?.remove();

    modal.querySelectorAll('form, input, label, .klaviyo-bis-modal-subscribe, [class*="subscribe"]').forEach((el) => {
      if (el.closest('[data-klaviyo-bis-success-panel]')) return;
      el.hidden = true;
      el.style.display = 'none';
    });

    findBisSubmitButtons(modal).forEach((el) => {
      el.hidden = true;
      el.style.display = 'none';
    });

    let successPanel = modal.querySelector('[data-klaviyo-bis-success-panel]');
    if (!successPanel) {
      successPanel = document.createElement('div');
      successPanel.dataset.klaviyoBisSuccessPanel = 'true';
      successPanel.className = 'klaviyo-bis-success-panel';
      successPanel.innerHTML =
        '<div class="klaviyo-bis-success-panel__icon" aria-hidden="true"></div>' +
        '<p class="klaviyo-bis-success-panel__message" role="status"></p>';
      modal.appendChild(successPanel);
    }

    const messageEl = successPanel.querySelector('.klaviyo-bis-success-panel__message');
    if (messageEl) messageEl.textContent = successLabel;

    successPanel.hidden = false;
    successPanel.style.display = '';
    successPanel.tabIndex = -1;
    successPanel.focus();
  }

  async function subscribeBackInStock(email) {
    const productId = config().productId;
    const variantId = getSelectedVariantId();

    if (!productId || !variantId) {
      throw new Error('Missing product or variant.');
    }

    const catalogVariantId = toCatalogVariantId(productId, variantId);

    internalBisRequest = true;

    try {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 15000);

      const response = await fetch(apiUrl(), {
        method: 'POST',
        signal: controller.signal,
        headers: {
          accept: 'application/json',
          revision: API_REVISION,
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email },
                },
              },
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: catalogVariantId,
                },
              },
            },
          },
        }),
      });

      window.clearTimeout(timeout);

      if (response.status === 202 || response.ok) {
        if (window.klaviyo?.identify) window.klaviyo.identify({ $email: email });
        return;
      }

      let detail = 'Unable to subscribe. Please try again.';
      try {
        const body = await response.json();
        detail = body?.errors?.[0]?.detail || detail;
      } catch (_) {}

      throw new Error(detail);
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }
      throw error;
    } finally {
      internalBisRequest = false;
    }
  }

  async function handleBisSubmission(modal, email) {
    if (!email) {
      showFormError(modal, 'Please enter a valid email address.');
      return;
    }

    clearFormError(modal);
    setBisBusy(modal, true);

    try {
      await subscribeBackInStock(email);
      showSuccess(modal);
    } catch (error) {
      showFormError(modal, error.message || 'Unable to subscribe. Please try again.');
    } finally {
      setBisBusy(modal, false);
    }
  }

  function extractEmailFromRequestBody(body) {
    if (!body) return '';

    if (body instanceof FormData) {
      return String(body.get('email') || '').trim();
    }

    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body);
        return String(
          parsed?.email ||
            parsed?.data?.attributes?.profile?.data?.attributes?.email ||
            '',
        ).trim();
      } catch (_) {
        return String(new URLSearchParams(body).get('email') || '').trim();
      }
    }

    return '';
  }

  function isLegacyBisRequest(url) {
    return typeof url === 'string' && url.includes('back-in-stock/subscribe');
  }

  function isClientBisRequest(url) {
    return typeof url === 'string' && url.includes('client/back-in-stock-subscriptions');
  }

  function rewriteBisBody(body) {
    if (!body || typeof body !== 'string') return body;

    try {
      const parsed = JSON.parse(body);
      const productId = config().productId;
      const variantId = getSelectedVariantId();
      if (!parsed?.data || !productId || !variantId) return body;

      parsed.data.relationships = parsed.data.relationships || {};
      parsed.data.relationships.variant = {
        data: {
          type: 'catalog-variant',
          id: toCatalogVariantId(productId, variantId),
        },
      };

      return JSON.stringify(parsed);
    } catch (_) {
      return body;
    }
  }

  function completeMockXhr(xhr, ok) {
    xhr.status = ok ? 202 : 400;
    xhr.responseText = '';
    xhr.readyState = 4;
    xhr.dispatchEvent(new Event('readystatechange'));
    xhr.dispatchEvent(new Event('load'));
    xhr.dispatchEvent(new Event('loadend'));
  }

  function installLegacyNetworkIntercept() {
    if (window.__klaviyoBisLegacyIntercept) return;
    window.__klaviyoBisLegacyIntercept = true;

    const originalFetch = window.fetch.bind(window);
    window.fetch = function (...args) {
      const input = args[0];
      const init = args[1];
      const url = typeof input === 'string' ? input : input?.url;

      if (internalBisRequest) {
        return originalFetch.apply(this, args);
      }

      if (isLegacyBisRequest(url)) {
        const email = extractEmailFromRequestBody(init?.body);
        if (email) {
          return subscribeBackInStock(email).then(
            () => new Response(null, { status: 202 }),
            () => new Response(null, { status: 400 }),
          );
        }
      }

      if (isClientBisRequest(url)) {
        if (init && typeof init.body === 'string') {
          return originalFetch(url, Object.assign({}, init, { body: rewriteBisBody(init.body) }));
        }

        if (typeof Request !== 'undefined' && input instanceof Request) {
          return input.text().then((text) =>
            originalFetch(url, {
              method: input.method,
              headers: input.headers,
              body: rewriteBisBody(text),
              credentials: input.credentials,
              mode: input.mode,
            }),
          );
        }
      }

      return originalFetch.apply(this, args);
    };

    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.__klaviyoBisUrl = url;
      return originalOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function (body) {
      const url = this.__klaviyoBisUrl || '';

      if (internalBisRequest) {
        return originalSend.call(this, body);
      }

      if (isLegacyBisRequest(url)) {
        const email = extractEmailFromRequestBody(body);
        if (email) {
          subscribeBackInStock(email)
            .then(() => completeMockXhr(this, true))
            .catch(() => completeMockXhr(this, false));
          return;
        }
      }

      if (isClientBisRequest(url) && typeof body === 'string') {
        return originalSend.call(this, rewriteBisBody(body));
      }

      return originalSend.call(this, body);
    };
  }

  function onBisSubmit(event) {
    const modal = findBisModal(event.target);
    if (!modal) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const email = findBisEmailInput(modal)?.value?.trim() || '';
    handleBisSubmission(modal, email);
  }

  function onBisClick(event) {
    const button = event.target.closest(
      '#klaviyo-bis-modal button, [id*="klaviyo-bis-modal"] button, .klaviyo-bis-modal button',
    );
    if (!button || button.type === 'button') return;

    const modal = findBisModal(button);
    if (!modal) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const email = findBisEmailInput(modal)?.value?.trim() || '';
    handleBisSubmission(modal, email);
  }

  document.addEventListener('submit', onBisSubmit, true);
  document.addEventListener('click', onBisClick, true);
  installLegacyNetworkIntercept();
})();
